import { OverlayModule, ConnectedPosition } from '@angular/cdk/overlay';
import {
  Component,
  computed,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  Output,
  signal,
  ViewChild,
  forwardRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';

import { SuiFormControlBase } from '../shared/cva-helper';
import {
  SuiFormFieldControl,
  SUI_FORM_FIELD_CONTROL,
} from '../form-field/form-field-control';

/** A suggestion / selected tag. Created free-text tags become `{ label, value }` with the text as both. */
export interface TagOption<T = unknown> {
  label: string;
  value: T;
}

@Component({
  selector: 'sui-tags',
  standalone: true,
  imports: [OverlayModule, FaIconComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SuiTagsComponent),
      multi: true,
    },
    {
      provide: SUI_FORM_FIELD_CONTROL,
      useExisting: forwardRef(() => SuiTagsComponent),
    },
  ],
  templateUrl: './sui-tags.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './sui-tags.component.scss',
})
export class SuiTagsComponent
  extends SuiFormControlBase<unknown[]>
  implements SuiFormFieldControl
{
  private static _idCounter = 0;
  private readonly _uid = `sui-tags-${++SuiTagsComponent._idCounter}`;
  private readonly _host = inject(ElementRef<HTMLElement>);

  readonly faXmark = faXmark;
  readonly controlType = 'tags';

  @ViewChild('tagInput') private tagInput?: ElementRef<HTMLInputElement>;

  @Input() id = '';
  @Input() placeholder = 'Add a tag…';
  @Input() loading = false;
  /**
   * Show a trailing clear-all button (mass-remove). Off by default — both real
   * consumers disable clear; opt in for the
   * combobox-style affordance. Cleared chips return to the suggestion pool.
   */
  @Input() clearable = false;
  /** Equality for value matching — supply for object-valued suggestions. */
  @Input() compareWith: (a: unknown, b: unknown) => boolean = Object.is;

  /** Emits the typed term (seam for a future server-populated pool — no fetch wired in v1). */
  @Output() searchChange = new EventEmitter<string>();

  @Input()
  set disabled(val: boolean) {
    this.setDisabledState(val);
  }
  get disabled(): boolean {
    return this.isDisabled();
  }

  /** Seed suggestions. The session pool grows as tags are created / removed. */
  @Input()
  set options(val: TagOption[]) {
    this._options.set(val ?? []);
  }
  private readonly _options = signal<TagOption[]>([]);

  // Session-accumulated tags beyond [options]: created free-text + removed tags
  // (so a removed chip — created or a preset not in [options] — resurfaces as a suggestion).
  private readonly _extra = signal<TagOption[]>([]);

  readonly query = signal('');
  // Dropdown visibility intent (gated further by a non-empty filtered pool + not disabled).
  private readonly _open = signal(false);
  // Keyboard highlight; -1 = nothing highlighted (Enter creates rather than picking).
  readonly activeIndex = signal(-1);
  private readonly _focused = signal(false);

  readonly overlayPositions: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
      offsetY: 4,
    },
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom',
      offsetY: -4,
    },
  ];

  private get selectedValues(): unknown[] {
    return (this.value() as unknown[]) ?? [];
  }

  private isSelected(value: unknown): boolean {
    return this.selectedValues.some((v) => this.compareWith(v, value));
  }

  // [options] ∪ session extras (created / removed), deduped by value.
  private readonly pool = computed<TagOption[]>(() => {
    const result = [...this._options()];
    for (const extra of this._extra()) {
      if (!result.some((o) => this.compareWith(o.value, extra.value))) {
        result.push(extra);
      }
    }
    return result;
  });

  // The chips: each selected value resolved to its pool option (fallback label = the value itself).
  readonly selectedOptions = computed<TagOption[]>(() => {
    const pool = this.pool();
    return this.selectedValues.map((v) => {
      const match = pool.find((o) => this.compareWith(o.value, v));
      return match ?? { label: String(v), value: v };
    });
  });

  // The dropdown contents: pool minus selected, filtered by the typed query.
  readonly filteredPool = computed<TagOption[]>(() => {
    const q = this.query().toLowerCase().trim();
    return this.pool().filter((o) => {
      if (this.isSelected(o.value)) return false;
      return !q || o.label.toLowerCase().includes(q);
    });
  });

  // "Only when there's something to suggest" — no empty / "no items" state.
  readonly dropdownOpen = computed(
    () => this._open() && !this.isDisabled() && this.filteredPool().length > 0,
  );

  get effectiveId(): string {
    return this.id || this._uid;
  }

  get overlayWidth(): number {
    return this._host.nativeElement.offsetWidth;
  }

  // SuiFormFieldControl surface.
  get focused(): boolean {
    return this._focused();
  }
  get empty(): boolean {
    return !this.selectedValues.length;
  }
  get errorState(): boolean {
    return !!(this.ngControl?.invalid && this.ngControl?.touched);
  }

  onQueryInput(event: Event): void {
    const term = (event.target as HTMLInputElement).value;
    this.query.set(term);
    this.activeIndex.set(-1);
    this._open.set(true);
    this.searchChange.emit(term);
  }

  onKeydown(event: KeyboardEvent): void {
    if (this.isDisabled()) return;
    switch (event.key) {
      case 'Enter':
        event.preventDefault();
        this.commitActiveOrText();
        break;
      case 'Backspace':
        if (!this.query() && this.selectedValues.length) {
          event.preventDefault();
          this.removeLast();
        }
        break;
      case 'ArrowDown':
        if (this.filteredPool().length) {
          event.preventDefault();
          this._open.set(true);
          this.activeIndex.set(
            (this.activeIndex() + 1) % this.filteredPool().length,
          );
        }
        break;
      case 'ArrowUp':
        if (this.filteredPool().length) {
          event.preventDefault();
          const len = this.filteredPool().length;
          this.activeIndex.set((this.activeIndex() - 1 + len) % len);
        }
        break;
      case 'Escape':
        this._open.set(false);
        break;
    }
  }

  // Enter: pick the highlighted suggestion if any; else commit the typed text.
  private commitActiveOrText(): void {
    const active = this.activeIndex();
    const pool = this.filteredPool();
    if (active >= 0 && active < pool.length) {
      this.selectOption(pool[active]);
      return;
    }
    const text = this.query().trim();
    if (!text) return;
    // Reuse an existing pool entry if the text matches its label (dedup; no duplicate tag).
    const match = this.pool().find(
      (o) => o.label.toLowerCase() === text.toLowerCase(),
    );
    this.selectOption(match ?? { label: text, value: text });
  }

  selectOption(opt: TagOption): void {
    this.addExtra(opt);
    if (!this.isSelected(opt.value)) {
      this.setValue([...this.selectedValues, opt.value]);
    }
    this.query.set('');
    this.activeIndex.set(-1);
    this.focusInput();
  }

  removeChip(opt: TagOption, event: Event): void {
    event.stopPropagation();
    // Keep it in the pool so it resurfaces as a suggestion.
    this.addExtra(opt);
    this.setValue(
      this.selectedValues.filter((v) => !this.compareWith(v, opt.value)),
    );
  }

  clearAll(event: Event): void {
    event.stopPropagation();
    // Keep every cleared tag in the pool so they resurface as suggestions.
    for (const opt of this.selectedOptions()) this.addExtra(opt);
    this.setValue([]);
    this.query.set('');
    this.activeIndex.set(-1);
    this.focusInput();
  }

  private removeLast(): void {
    const chips = this.selectedOptions();
    const last = chips[chips.length - 1];
    if (last) this.addExtra(last);
    this.setValue(this.selectedValues.slice(0, -1));
  }

  // Record a tag in the session pool extension (deduped by value, and only when
  // not already a provided [option]).
  private addExtra(opt: TagOption): void {
    if (this._options().some((o) => this.compareWith(o.value, opt.value)))
      return;
    if (this._extra().some((o) => this.compareWith(o.value, opt.value))) return;
    this._extra.set([...this._extra(), opt]);
  }

  // Fires for clicks anywhere outside the overlay pane — including the field
  // itself. Only close on a genuine outside click (clicking the field keeps the
  // dropdown so the user can keep typing); a focusout closes the rest.
  onOutsideClick(event: MouseEvent): void {
    if (this._host.nativeElement.contains(event.target as Node)) return;
    this._open.set(false);
  }

  // Click in the empty field area focuses the input (chips/remove buttons handle their own clicks).
  onFieldMousedown(event: MouseEvent): void {
    if (event.target === this.tagInput?.nativeElement) return;
    if ((event.target as HTMLElement).closest('.chip')) return;
    event.preventDefault();
    this.focusInput();
  }

  private focusInput(): void {
    this.tagInput?.nativeElement.focus();
  }

  @HostListener('focusin')
  onFocusIn(): void {
    this._focused.set(true);
    if (this.filteredPool().length) this._open.set(true);
  }

  @HostListener('focusout')
  onFocusOut(): void {
    this._focused.set(false);
    this._open.set(false);
    this.query.set('');
    this.activeIndex.set(-1);
    this.markTouched();
  }
}
